import { authHttpRequest, makeRequest } from './Request';
import debounce from 'lodash.debounce';
import {
  getCacheForKey,
  REQUEST_STATUS,
  setCacheForKey,
  attachListener,
  removeListener
} from './CacheManager';
import { getShowcaseId } from './GroupCatalogueAmalgam';
import { getGlobalDefaultMarginFromCache } from './ResellerProfile';

const PRODUCT_MARGIN_BASE_KEY = 'PRODUCT_MARGINS';
const pendingProductIds = [];

const getProductMarginCacheKey = ({ productId }) => `${PRODUCT_MARGIN_BASE_KEY}/${productId}`;

const getFinalProductMargin = (data) => {
  const defaultMargin = getGlobalDefaultMarginFromCache();
  if (defaultMargin > 0) {
    if (typeof data !== 'number') {
      return defaultMargin;
    }
  }

  return data;
};

export const attachProductMarginListener = ({ productId, listener }) => {
  attachListener({ key: getProductMarginCacheKey({ productId }), listener });
};

export const removeProductMarginListener = ({ productId, listener }) => {
  removeListener({ key: getProductMarginCacheKey({ productId }), listener });
};

export const getProductMarginDataFromCache = ({ productId }) =>
  getCacheForKey(getProductMarginCacheKey({ productId }));

export const getProductMarginDataWithDefaultMarginFromCache = ({ productId }) => {
  const { status, data } = getProductMarginDataFromCache({ productId }) || {};
  return {
    status,
    data: getFinalProductMargin(data)
  };
};

const writeDataToCache = ({ productId, status, data }) => {
  const { status: cachedStatus, data: cachedData } =
    getProductMarginDataFromCache({ productId }) || {};

  setCacheForKey(
    {
      key: getProductMarginCacheKey({ productId }),
      value: {
        status: status || cachedStatus,
        data: typeof data === 'number' ? data : cachedData
      }
    },
    { notify: true }
  );
};

const updateProductMarginsApi = (params) =>
  authHttpRequest({
    method: 'PUT',
    url: '/api/reseller/product/margin',
    params
  });

export const updateProductMargins = ({ productIds, margin }) =>
  makeRequest({
    promiseCacheKey: 'PUT_PRODUCT_MARGINS',
    apiFunc: updateProductMarginsApi,
    productIds,
    margin,
    groupShowcaseId: getShowcaseId()
  }).then(() => {
    productIds.forEach((productId) => {
      writeDataToCache({ productId, data: margin });
    });
  });

const fetchProductMarginsApi = (params) =>
  authHttpRequest({
    method: 'POST',
    url: '/api/reseller/products/margin',
    params
  })
    .then(({ productIdMarginMap }) => {
      Object.keys(productIdMarginMap).forEach((productId) => {
        const margin = productIdMarginMap[productId];
        writeDataToCache({ productId, status: REQUEST_STATUS.COMPLETE, data: margin });
      });
    })
    .catch(() => {
      const { productIds } = params;
      if (!Array.isArray(productIds)) {
        return;
      }
      productIds.forEach((productId) => {
        writeDataToCache({ productId, status: REQUEST_STATUS.ERROR });
      });
    });

const productMarginDebouncer = debounce(() => {
  const productIdsForRequest = pendingProductIds.splice(0, pendingProductIds.length);
  productIdsForRequest.forEach((productId) => {
    writeDataToCache({ productId, status: REQUEST_STATUS.LOADING });
  });

  makeRequest({
    promiseCacheKey: `GET_PRODUCT_MARGINS_${Date.now()}`,
    apiFunc: fetchProductMarginsApi,
    productIds: productIdsForRequest,
    groupShowcaseId: getShowcaseId()
  });
}, 200);

export const fetchProductMargins = (productId) => {
  const { status } = getCacheForKey(getProductMarginCacheKey({ productId })) || {};
  // Request already in progress or completed for this productId, ignore it
  if (status && status !== REQUEST_STATUS.ERROR) {
    return;
  }

  writeDataToCache({ productId, status: REQUEST_STATUS.PENDING });
  pendingProductIds.push(productId);

  productMarginDebouncer();
};

export const getProductMarginFromCache = ({ productId }) => {
  // Apply margin only if the product's price exists
  const { data } = getProductMarginDataFromCache({ productId }) || {};
  return getFinalProductMargin(data);
};
